import { Injectable } from '@angular/core';
import { retryFailedCall } from '@common';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  DashboardApiPicker,
  DashboardInstallationData,
  DashboardInstallationsPayload,
  DashboardInstallationsSums,
  DashboardInstallationsSumsPayload,
  DashboardInstallationsSumsResponse,
  ERROR_SNAPSHOT_NOT_FOUND,
  ErrorType,
  GenericDataResponse,
  GetResponse,
  MultipleTileTypesResponse,
  SubscriptionStatus,
  VitoguideBackendSettingsApiPicker,
} from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';
import { combineLatest, from, MonoTypeOperatorFunction, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  filter,
  map,
  mergeMap,
  retry,
  retryWhen,
  scan,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { DashboardDialogService } from '../../+dashboard/dashboard-view/dashboard-dialog.service';
import { BookingStore, BookingSubscription } from '../booking';
import { BookingSubscriptionPackageOptions, RemoteConfigStore } from '../remote-config';
import { DashboardActions } from './dashboard.actions';
import {
  DashboardTileOrder,
  DashboardTileType,
  dashboardTileTypeToDashboardTileObjectMapper,
  defaultDashboardTilesOrder,
  InstallationsForMultipleTileCompletePayload,
  InstallationWithFaultTabsType,
} from './dashboard.model';
import { DashboardStore } from './dashboard.store';

@Injectable()
export class DashboardEffects {
  private static DASHBOARD_TILES_ORDER_BASE_URL = 'dashboardTilesOrder';
  fetchTilesOrder$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.fetchTilesOrder.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.bookingStore.hasAccessToBooking$.pipe(take(1)),
          this.remoteConfigStore.typeOfBookingPackage$.pipe(take(1)),
        ])
      ),
      switchMap(([_action, hasAccessToBooking, bookingSubscriptionPackageOption]) =>
        this.settingApiPicker
          .instance()
          .getKeyValueStorage(`${DashboardEffects.DASHBOARD_TILES_ORDER_BASE_URL}`)
          .pipe(
            retryFailedCall(3),
            map((response: GetResponse<DashboardTileOrder[]>) => {
              const tilesOrder: DashboardTileOrder[] = this.concealContractsEndingSoonTileForCommercialOrFlatRateSubscription(
                response.data,
                hasAccessToBooking,
                bookingSubscriptionPackageOption
              );

              return DashboardActions.fetchTilesOrder.success({ tilesOrder });
            }),
            catchError((error) => {
              const resourceNotFound = error.errorType === ErrorType.RESOURCE_NOT_FOUND;
              if (resourceNotFound) {
                const tilesOrder: DashboardTileOrder[] = this.concealContractsEndingSoonTileForCommercialOrFlatRateSubscription(
                  defaultDashboardTilesOrder,
                  hasAccessToBooking,
                  bookingSubscriptionPackageOption
                );

                return of(DashboardActions.fetchTilesOrder.success({ tilesOrder: tilesOrder }));
              }

              return of(DashboardActions.fetchTilesOrder.error({ error }));
            })
          )
      )
    );
  });

  saveTilesOrder$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.saveTilesOrder.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.bookingStore.hasAccessToBooking$.pipe(take(1)),
          this.remoteConfigStore.typeOfBookingPackage$.pipe(take(1)),
        ])
      ),
      switchMap(([action, hasAccessToBooking, bookingSubscriptionPackageOption]) => {
        const tilesOrder: DashboardTileOrder[] = this.concealContractsEndingSoonTileForCommercialOrFlatRateSubscription(
          action.tilesOrder,
          hasAccessToBooking,
          bookingSubscriptionPackageOption
        );

        let payload: GetResponse<DashboardTileOrder[]> = {
          data: tilesOrder,
        };

        return this.settingApiPicker
          .instance()
          .setKeyValueStorage(`${DashboardEffects.DASHBOARD_TILES_ORDER_BASE_URL}`, payload)
          .pipe(
            map(() => {
              if (action.showToastMsg) {
                this.toastService.info({
                  message: this.translateService.instant(
                    '+DASHBOARD.DASHBOARD_CONFIGURATION_MODAL.SAVE_SETTINGS.SUCCESS'
                  ),
                });
              }

              return DashboardActions.saveTilesOrder.success();
            }),
            catchError((err) => {
              if (action.showToastMsg) {
                this.toastService.error({
                  message: this.translateService.instant(
                    '+DASHBOARD.DASHBOARD_CONFIGURATION_MODAL.SAVE_SETTINGS.ERROR'
                  ),
                });
              }

              return of(DashboardActions.saveTilesOrder.error({ error: err }));
            })
          );
      })
    );
  });

  getDashboard$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.getDashboard.attempt),
      switchMap(() =>
        this.dashboardApiPicker
          .instance()
          .getDashboard()
          .pipe(
            retry(2),
            map(
              (response) => DashboardActions.getDashboard.success({ columns: response.data }),
              catchError((error) => of(DashboardActions.getDashboard.error({ error })))
            )
          )
      )
    );
  });

  initInstallationsForMultipleTileTypes$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.initInstallationsForMultipleTileTypes.attempt),
      concatLatestFrom(() =>
        this.dashboardStore.getColumnInstallations(InstallationWithFaultTabsType.Locked)
      ),
      switchMap(([action, lockedInstallations]) => {
        const requests: Observable<MultipleTileTypesResponse>[] = [];
        action.tileTypes.map((tileType) => {
          const mappedTileObject = dashboardTileTypeToDashboardTileObjectMapper[tileType];
          if (mappedTileObject.tabs) {
            mappedTileObject.tabs.map((tabItem) => {
              requests.push(
                this.dashboardApiPicker
                  .instance()
                  .fetchInstallations(
                    {
                      offset: 0,
                      limit: action.maxItemsPerFetch,
                      filter: tabItem.filter,
                    },
                    !!mappedTileObject.withQuickView
                  )
                  .pipe(
                    this.fetchDashboardWhenSnapshotExpired(),
                    catchError((err) => {
                      return of({
                        tileType: null,
                        parentTile: tileType,
                        tab: tabItem.storeState,
                        data: null,
                        error: err,
                      });
                    }),
                    map((data: MultipleTileTypesResponse) => {
                      return {
                        ...data,
                        tileType: null,
                        parentTile: tileType,
                        tab: tabItem.storeState,
                        error: data.error ?? null,
                      };
                    })
                  )
              );
            });
          } else {
            requests.push(
              this.dashboardApiPicker
                .instance()
                .fetchInstallations(
                  {
                    offset: 0,
                    limit: action.maxItemsPerFetch,
                    filter: dashboardTileTypeToDashboardTileObjectMapper[tileType].filter,
                  },
                  !!mappedTileObject.withQuickView
                )
                .pipe(
                  this.fetchDashboardWhenSnapshotExpired(),
                  catchError((err) => {
                    return of({
                      tileType: tileType,
                      parentTile: null,
                      tab: null,
                      data: null,
                      error: err,
                    });
                  }),
                  map((data: MultipleTileTypesResponse) => {
                    return {
                      ...data,
                      tileType: tileType,
                      parentTile: null,
                      tab: null,
                    };
                  })
                )
            );
          }
        });

        return from(requests).pipe(
          mergeMap((request: Observable<GenericDataResponse<DashboardInstallationData[]>>) => {
            return request.pipe(
              catchError((err) => {
                return request.pipe(
                  map((data: MultipleTileTypesResponse) => {
                    return {
                      tileType: data.tileType ?? null,
                      parentTile: data.parentTile ?? null,
                      tab: data.tab ?? null,
                      data: null,
                      error: err,
                    };
                  })
                );
              })
            );
          }),
          scan(
            (
              acc: InstallationsForMultipleTileCompletePayload[],
              val: MultipleTileTypesResponse
            ): InstallationsForMultipleTileCompletePayload[] => {
              return [
                ...acc,
                {
                  tileType: val.parentTile ?? val.tileType,
                  tab: val.tab ?? null,
                  installations:
                    val.parentTile === DashboardTileType.InstallationsWithFault &&
                    val.tab === InstallationWithFaultTabsType.Locked
                      ? lockedInstallations
                      : val.parentTile === DashboardTileType.InstallationsWithFault &&
                        val.tab === InstallationWithFaultTabsType.All
                      ? val.data.concat(lockedInstallations)
                      : val.data ?? [],

                  error: val.error ?? null,
                  maxItemsPerFetch: action.maxItemsPerFetch,
                },
              ];
            },
            []
          ),
          filter((combinedRequests: InstallationsForMultipleTileCompletePayload[]) => {
            let expectedRequestsCounter = 0;

            action.tileTypes.forEach((tileType: DashboardTileType) => {
              if (dashboardTileTypeToDashboardTileObjectMapper[tileType].tabs) {
                expectedRequestsCounter +=
                  dashboardTileTypeToDashboardTileObjectMapper[tileType].tabs.length;
              } else {
                expectedRequestsCounter++;
              }
            });

            return combinedRequests.length === expectedRequestsCounter;
          }),
          map((result: InstallationsForMultipleTileCompletePayload[]) => {
            return DashboardActions.initInstallationsForMultipleTileTypes.complete({
              data: result,
            });
          })
        );
      })
    );
  });

  initInstallationsForSingleTileType$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.installationsForSingleTileType.init),
      concatLatestFrom(() =>
        this.dashboardStore.getColumnInstallations(InstallationWithFaultTabsType.Locked)
      ),
      switchMap(([action, lockedInstallations]) => {
        const mappedTileObject = dashboardTileTypeToDashboardTileObjectMapper[action.tileType];
        const filter = action.tab
          ? mappedTileObject.tabs.find((tab) => tab.storeState === action.tab).filter
          : mappedTileObject.filter;

        const offset = 0;
        const payload: DashboardInstallationsPayload = {
          offset,
          limit: action.maxItemsPerFetch,
          filter,
        };

        return this.dashboardApiPicker
          .instance()
          .fetchInstallations(payload, !!mappedTileObject.withQuickView)
          .pipe(
            this.fetchDashboardWhenSnapshotExpired(),
            map((response: GetResponse<DashboardInstallationData[]>) => {
              let installations: DashboardInstallationData[] = response.data;

              return DashboardActions.installationsForSingleTileType.success({
                tileType: action.tileType,
                maxItemsPerFetch: action.maxItemsPerFetch,
                installations:
                  action.tileType === DashboardTileType.InstallationsWithFault &&
                  action.tab === InstallationWithFaultTabsType.Locked
                    ? lockedInstallations
                    : action.tileType === DashboardTileType.InstallationsWithFault &&
                      action.tab === InstallationWithFaultTabsType.All
                    ? installations.concat(lockedInstallations)
                    : installations,
                tab: action.tab ?? null,
              });
            }),
            catchError((error) => {
              return of(
                DashboardActions.installationsForSingleTileType.error({
                  tileType: action.tileType,
                  tab: action.tab ?? null,
                  error,
                })
              );
            })
          );
      })
    );
  });

  fetchInstallationsForSingleTileType$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.installationsForSingleTileType.fetch),
      concatLatestFrom(() => this.dashboardStore.state$),
      switchMap(([action, state]) => {
        const mappedTileObject = dashboardTileTypeToDashboardTileObjectMapper[action.tileType];
        const tileState = action.tab
          ? state[mappedTileObject.storeState].value[action.tab]
          : state[mappedTileObject.storeState];
        const filter = action.tab
          ? mappedTileObject.tabs.find((tab) => tab.storeState === action.tab).filter
          : mappedTileObject.filter;

        if (tileState.canFetch) {
          const offset = tileState.value.length ?? 0;
          const payload: DashboardInstallationsPayload = {
            offset,
            limit: action.maxItemsPerFetch,
            filter,
          };

          return this.dashboardApiPicker
            .instance()
            .fetchInstallations(payload, !!mappedTileObject.withQuickView)
            .pipe(
              retryFailedCall(3),
              map((response: GetResponse<DashboardInstallationData[]>) => {
                let installations: DashboardInstallationData[] = response.data;

                return DashboardActions.installationsForSingleTileType.success({
                  tileType: action.tileType,
                  maxItemsPerFetch: action.maxItemsPerFetch,
                  installations,
                  tab: action.tab ?? null,
                });
              }),
              catchError((error) => {
                if (error.errorType === ERROR_SNAPSHOT_NOT_FOUND) {
                  this.dashboardDialogService.openRefreshDialog();
                } else {
                  return of(
                    DashboardActions.installationsForSingleTileType.error({
                      tileType: action.tileType,
                      tab: action.tab ?? null,
                      error,
                    })
                  );
                }
              })
            );
        }

        return of(
          DashboardActions.installationsForSingleTileType.noDataToFetch({
            tileType: action.tileType,
            tab: action.tab ?? null,
          })
        );
      })
    );
  });

  initContractsEndingSoon$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.contractEndingSoon.init),
      tap(() => {
        this.bookingStore.loadInstallations();
        this.bookingStore.getAllVitoguideSubscriptions();
      }),
      concatMap((action) => {
        return this.bookingStore.loaded$.pipe(
          filter((loaded) => loaded === true),
          concatLatestFrom(() => [this.bookingStore.subscriptions$]),
          mergeMap(([_loaded, bookingSubscriptions]) => {
            const offset = 0;
            const payload: DashboardInstallationsPayload = {
              offset,
              limit: action.maxItemsPerFetch,
              filter:
                dashboardTileTypeToDashboardTileObjectMapper[DashboardTileType.ContractsEndingSoon]
                  .filter,
            };

            return this.dashboardApiPicker
              .instance()
              .fetchInstallations(payload)
              .pipe(
                this.fetchDashboardWhenSnapshotExpired(),
                map((response) => {
                  const installations: DashboardInstallationData[] = this.getDashboardInstallationsWithSubscriptions(
                    response,
                    bookingSubscriptions
                  );

                  installations.sort(
                    (a, b) =>
                      new Date(a.subscriptionExpirationDate).getTime() -
                      new Date(b.subscriptionExpirationDate).getTime()
                  );

                  return DashboardActions.contractEndingSoon.success({
                    maxItemsPerFetch: action.maxItemsPerFetch,
                    installations,
                  });
                }),
                catchError((error) => {
                  return of(
                    DashboardActions.contractEndingSoon.error({
                      error,
                    })
                  );
                })
              );
          })
        );
      })
    );
  });

  fetchContractsEndingSoon$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.contractEndingSoon.fetch),

      switchMap((action) => {
        return this.bookingStore.loaded$.pipe(
          filter((loaded) => loaded === true),
          concatLatestFrom(() => [this.bookingStore.subscriptions$, this.dashboardStore.state$]),
          mergeMap(([_loaded, bookingSubscriptions, state]) => {
            const tileState =
              state[
                dashboardTileTypeToDashboardTileObjectMapper[DashboardTileType.ContractsEndingSoon]
                  .storeState
              ];
            const filter =
              dashboardTileTypeToDashboardTileObjectMapper[DashboardTileType.ContractsEndingSoon]
                .filter;

            if (tileState.canFetch) {
              const offset = tileState.value.length ?? 0;
              const payload: DashboardInstallationsPayload = {
                offset,
                limit: action.maxItemsPerFetch,
                filter,
              };

              return this.dashboardApiPicker
                .instance()
                .fetchInstallations(payload)
                .pipe(
                  retryFailedCall(3),
                  map((response) => {
                    let installations: DashboardInstallationData[] = this.getDashboardInstallationsWithSubscriptions(
                      response,
                      bookingSubscriptions
                    );

                    return DashboardActions.contractEndingSoon.success({
                      maxItemsPerFetch: action.maxItemsPerFetch,
                      installations,
                    });
                  }),
                  catchError((error) => {
                    if (error.errorType === ERROR_SNAPSHOT_NOT_FOUND) {
                      this.dashboardDialogService.openRefreshDialog();
                    } else {
                      return of(
                        DashboardActions.contractEndingSoon.error({
                          error,
                        })
                      );
                    }
                  })
                );
            }

            return of(DashboardActions.contractEndingSoon.noDataToFetch());
          })
        );
      })
    );
  });

  getInstallationsCounterForTiles$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.countInstallationsForTiles.init),
      switchMap((action) => {
        let payload: DashboardInstallationsSumsPayload[] = [];

        if (action.tab) {
          const tileObject = dashboardTileTypeToDashboardTileObjectMapper[action.tiles[0]];
          const tabObject =
            dashboardTileTypeToDashboardTileObjectMapper[action.tiles[0]].tabs[action.tab];
          payload.push({
            name: `${tileObject.storeState}-${action.tab}`,
            filters: {
              [tabObject.filter.filterName]: tabObject.filter.values,
            },
          });
        } else {
          action.tiles.forEach((tile) => {
            if (dashboardTileTypeToDashboardTileObjectMapper[tile].tabs) {
              dashboardTileTypeToDashboardTileObjectMapper[tile].tabs.forEach((tab) => {
                payload.push({
                  name: `${dashboardTileTypeToDashboardTileObjectMapper[tile].storeState}-${tab.storeState}`,
                  filters: {
                    [tab.filter.filterName]: tab.filter.values,
                  },
                });
              });
            } else {
              payload.push({
                name: `${dashboardTileTypeToDashboardTileObjectMapper[tile].storeState}`,
                filters: {
                  [dashboardTileTypeToDashboardTileObjectMapper[tile].filter.filterName]:
                    dashboardTileTypeToDashboardTileObjectMapper[tile].filter.values,
                },
              });
            }
          });
        }

        const apiRangeLimit = 10;
        const chunkedTileTypes: DashboardInstallationsSumsPayload[][] = Array.from(
          { length: Math.ceil(payload.length / apiRangeLimit) },
          (_v, i) => payload.slice(i * apiRangeLimit, i * apiRangeLimit + apiRangeLimit)
        );

        const apiCalls: Observable<GenericDataResponse<
          DashboardInstallationsSumsResponse[]
        >> = from(chunkedTileTypes).pipe(
          mergeMap((chunkPayload: DashboardInstallationsSumsPayload[]) =>
            this.dashboardApiPicker.instance().countInstallationsForFilters(chunkPayload)
          )
        );

        return apiCalls.pipe(
          retryFailedCall(3),
          map((response: GetResponse<DashboardInstallationsSumsResponse[]>) => {
            const installationsSums: DashboardInstallationsSums[] = response.data.map(
              (item: DashboardInstallationsSumsResponse) => {
                const [tileType, tab] = item.name.split('-');

                return { tileType, tab, counter: item.value };
              }
            );

            return DashboardActions.countInstallationsForTiles.success({
              installationsSums,
            });
          }),
          catchError((error) => {
            return of(
              DashboardActions.countInstallationsForTiles.error({
                error,
              })
            );
          })
        );
      })
    );
  });

  fetchWhatsAppBannerInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.whatsAppBannerInfo.attempt),
      switchMap(() =>
        this.settingApiPicker
          .instance()
          .getWhatsAppBannerStatus()
          .pipe(
            map((data) => DashboardActions.whatsAppBannerInfo.success(data)),
            catchError((error) => {
              // Settings were not set yet
              if (error.statusCode === 404) {
                return of(DashboardActions.whatsAppBannerInfo.updateValue({ data: true }));
              }

              return of(DashboardActions.whatsAppBannerInfo.error({ error }));
            })
          )
      )
    );
  });

  setWhatsAppBannerInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.whatsAppBannerInfo.updateValue),
      switchMap((action) =>
        this.settingApiPicker
          .instance()
          .setWhatsAppBannerStatus(action.data)
          .pipe(
            map((data) => DashboardActions.whatsAppBannerInfo.success(data)),
            catchError((error) => of(DashboardActions.whatsAppBannerInfo.error({ error })))
          )
      )
    );
  });

  constructor(
    private actions$: Actions,
    private dashboardStore: DashboardStore,
    private remoteConfigStore: RemoteConfigStore,
    private bookingStore: BookingStore,
    private settingApiPicker: VitoguideBackendSettingsApiPicker,
    private dashboardApiPicker: DashboardApiPicker,
    private toastService: ViToastService,
    private dashboardDialogService: DashboardDialogService,
    private translateService: TranslateService
  ) {}

  private concealContractsEndingSoonTileForCommercialOrFlatRateSubscription(
    tilesOrder: DashboardTileOrder[],
    hasAccessToBooking: boolean,
    bookingSubscriptionPackageOption: BookingSubscriptionPackageOptions
  ): DashboardTileOrder[] {
    if (
      !hasAccessToBooking ||
      bookingSubscriptionPackageOption === BookingSubscriptionPackageOptions.Commercial ||
      bookingSubscriptionPackageOption === BookingSubscriptionPackageOptions.FlatRate
    ) {
      tilesOrder = tilesOrder.map(
        (tile: DashboardTileOrder): DashboardTileOrder =>
          tile.type === DashboardTileType.ContractsEndingSoon
            ? {
                ...tile,
                concealed: true,
              }
            : tile
      );
    } else {
      tilesOrder = tilesOrder.map(
        (tile: DashboardTileOrder): DashboardTileOrder =>
          tile.type === DashboardTileType.ContractsEndingSoon
            ? {
                ...tile,
                concealed: false,
              }
            : tile
      );
    }

    return tilesOrder;
  }

  private getDashboardInstallationsWithSubscriptions(
    response: GenericDataResponse<DashboardInstallationData[]>,
    bookingSubscriptions: any
  ): DashboardInstallationData[] {
    let installations: DashboardInstallationData[] = [];

    response.data.forEach((installation: DashboardInstallationData) => {
      const subscriptionOfInstallation = bookingSubscriptions.find(
        (subscription: BookingSubscription) =>
          (installation.id === subscription.installationId ||
            installation.device?.vin === subscription.boilerId) &&
          subscription.expirationDate &&
          (subscription.status === SubscriptionStatus.active ||
            subscription.status === SubscriptionStatus.canceled)
      );
      if (subscriptionOfInstallation) {
        installations.push({
          ...installation,
          subscriptionExpirationDate:
            installation.subscriptionExpirationDate ?? subscriptionOfInstallation.expirationDate,
          isTrialSubscription: subscriptionOfInstallation.trial,
        });
      }
    });

    return installations;
  }

  private fetchDashboardWhenSnapshotExpired(): MonoTypeOperatorFunction<
    GenericDataResponse<DashboardInstallationData[]>
  > {
    return retryWhen((errors) =>
      errors.pipe(
        delay(2000),
        scan((retryIndex, error) => {
          if (retryIndex === 2 || error.errorType !== ERROR_SNAPSHOT_NOT_FOUND) {
            throw error;
          }

          this.dashboardStore.fetchDashboard();

          return retryIndex + 1;
        }, 1)
      )
    );
  }
}
